import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import {
  Workflow,
  WorkflowRun,
  WorkflowService,
} from "../../services/WorkflowService";
import { ApplicationService } from "../../services/ApplicationService";
import { Application } from "../../model_gen/application";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import NewApplicationModal from "../Application/NewApplicationModal";
import { cn } from "../../lib/utils";
import {
  ArrowLeftIcon,
  CodeIcon,
  HistoryIcon,
  SettingsIcon,
  GitBranchIcon,
} from "lucide-react";
import { toast } from "sonner";
import WorkflowRunDetailModal from "../shared/WorkflowRunDetailModal";
import WorkflowScriptTab from "./WorkflowDetailTabs/WorkflowScriptTab";
import WorkflowSessionsTab, { SESSIONS_PAGE_SIZE } from "./WorkflowDetailTabs/WorkflowSessionsTab";
import WorkflowConfigTab from "./WorkflowDetailTabs/WorkflowConfigTab";
import WorkflowVersionsTab from "./WorkflowDetailTabs/WorkflowVersionsTab";
import { TRANSFORM_TEMPLATE, S3Version, DEFAULT_SCHEMA } from "./workflowConstants";

const WorkflowDetail: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();

  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [activeTab, setActiveTab] = useState("script");
  const [scriptSubTab, setScriptSubTab] = useState("code");
  const [script, setScript] = useState("");
  const [originalScript, setOriginalScript] = useState("");
  const [scriptLoading, setScriptLoading] = useState(true);
  const [inputSchema, setInputSchema] = useState("");
  const [originalInputSchema, setOriginalInputSchema] = useState("");
  const [outputSchema, setOutputSchema] = useState("");
  const [originalOutputSchema, setOriginalOutputSchema] = useState("");
  const [transformSession, setTransformSession] = useState("");
  const [originalTransformSession, setOriginalTransformSession] = useState("");
  const [transformEnabled, setTransformEnabled] = useState(false);
  const [originalTransformEnabled, setOriginalTransformEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [versions, setVersions] = useState<S3Version[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [deployingVersionId, setDeployingVersionId] = useState<string | null>(null);

  const [settingsName, setSettingsName] = useState("");
  const [settingsDescription, setSettingsDescription] = useState("");
  const [settingsTimeout, setSettingsTimeout] = useState(30);
  const [settingsApplicationId, setSettingsApplicationId] = useState<number | undefined>(undefined);
  const [savingSettings, setSavingSettings] = useState(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const [showNewAppModal, setShowNewAppModal] = useState(false);

  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsPage, setRunsPage] = useState(0);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);

  // --- Data loading ---

  const loadWorkflow = useCallback(() => {
    if (!apiKey || !workflowId) return;
    setLoading(true);

    let defaultSub: { unsubscribe: () => void } | null = null;

    const wfSub = WorkflowService.getWorkflow(Number(workflowId), () => {}, apiKey).subscribe(
      (wf: Workflow) => {
        if (wf) {
          setWorkflow(wf);
          setSettingsName(wf.workflow_name);
          setSettingsDescription(wf.description || "");
          setSettingsTimeout(wf.timeout_seconds || 30);
          setSettingsApplicationId(wf.application_id || undefined);
        }
        setLoading(false);
      },
      () => setLoading(false)
    );

    const loadDefault = () => {
      defaultSub = WorkflowService.getDefaultWorkflowScript(() => {}, apiKey).subscribe(
        (defaultData: any) => {
          if (defaultData?.code) {
            setScript(defaultData.code);
            setOriginalScript(defaultData.code);
          }
          setScriptLoading(false);
        },
        () => setScriptLoading(false)
      );
    };

    const scriptSub = WorkflowService.getWorkflowScript(
      Number(workflowId),
      () => {},
      apiKey
    ).subscribe(
      (data: any) => {
        if (data?.code) {
          setScript(data.code);
          setOriginalScript(data.code);
          const inSchema = data.input_schema || DEFAULT_SCHEMA;
          const outSchema = data.output_schema || DEFAULT_SCHEMA;
          setInputSchema(inSchema);
          setOriginalInputSchema(inSchema);
          setOutputSchema(outSchema);
          setOriginalOutputSchema(outSchema);
          setTransformEnabled(data.transform_enabled || false);
          setOriginalTransformEnabled(data.transform_enabled || false);
          const trans = data.transform_session || TRANSFORM_TEMPLATE;
          setTransformSession(trans);
          setOriginalTransformSession(trans);
          setScriptLoading(false);
        } else {
          loadDefault();
        }
      },
      () => loadDefault()
    );

    const appSub = ApplicationService.getApplications(() => {}, apiKey).subscribe(
      (response: any) => {
        const fetched = Array.isArray(response) ? response : (response.applications || []);
        setApplications(fetched);
      },
      () => {}
    );

    return () => {
      wfSub.unsubscribe();
      scriptSub.unsubscribe();
      appSub.unsubscribe();
      defaultSub?.unsubscribe();
    };
  }, [apiKey, workflowId]);

  useEffect(() => {
    const cleanup = loadWorkflow();
    return cleanup;
  }, [loadWorkflow]);

  const runsPageRef = useRef(runsPage);
  runsPageRef.current = runsPage;

  const loadRuns = useCallback((silent = false) => {
    if (!apiKey || !workflowId) return;
    if (!silent) setRunsLoading(true);
    const sub = WorkflowService.getWorkflowRunsByWorkflow(
      Number(workflowId),
      runsPageRef.current * SESSIONS_PAGE_SIZE,
      SESSIONS_PAGE_SIZE,
      () => {},
      apiKey
    ).subscribe(
      (data: any) => {
        setRuns(data?.runs || []);
        setRunsTotal(data?.total || 0);
        setRunsLoading(false);
      },
      () => setRunsLoading(false)
    );
    return () => sub.unsubscribe();
  }, [apiKey, workflowId]);

  useEffect(() => {
    const cleanup = loadRuns();
    const intervalId = setInterval(() => {
      if (activeTab === "sessions") loadRuns(true);
    }, 10000);
    return () => {
      if (cleanup) cleanup();
      clearInterval(intervalId);
    };
  }, [loadRuns, activeTab, runsPage]);

  const loadVersions = useCallback(() => {
    if (!apiKey || !workflowId) return;
    setVersionsLoading(true);
    WorkflowService.getWorkflowVersions(Number(workflowId), () => {}, apiKey).subscribe(
      (data: any) => {
        setVersions(data?.versions || []);
        setVersionsLoading(false);
      },
      () => setVersionsLoading(false)
    );
  }, [apiKey, workflowId]);

  // --- Handlers ---

  const handleDeployVersion = (versionId: string) => {
    if (!apiKey || !workflowId) return;
    setDeployingVersionId(versionId);
    WorkflowService.deployWorkflowVersion(Number(workflowId), versionId, () => {}, apiKey).subscribe(
      (data: any) => {
        setDeployingVersionId(null);
        toast.success("Version deployed");
        if (data?.code) { setScript(data.code); setOriginalScript(data.code); }
        if (data?.input_schema !== undefined) { const v = data.input_schema || DEFAULT_SCHEMA; setInputSchema(v); setOriginalInputSchema(v); }
        if (data?.output_schema !== undefined) { const v = data.output_schema || DEFAULT_SCHEMA; setOutputSchema(v); setOriginalOutputSchema(v); }
        if (data?.transform_session !== undefined) {
          setTransformEnabled(data.transform_enabled || false);
          setOriginalTransformEnabled(data.transform_enabled || false);
          const t = data.transform_session || TRANSFORM_TEMPLATE;
          setTransformSession(t);
          setOriginalTransformSession(t);
        }
        loadVersions();
      },
      (err: any) => {
        setDeployingVersionId(null);
        toast.error(`Failed to deploy: ${err?.message || "Unknown error"}`);
      }
    );
  };

  const handleSaveScript = () => {
    if (!apiKey || !workflowId) return;
    setSaving(true);
    const payload: Record<string, any> = {};
    if (script !== originalScript) payload.script = script;
    if (inputSchema !== originalInputSchema) payload.input_schema = inputSchema;
    if (outputSchema !== originalOutputSchema) payload.output_schema = outputSchema;
    if (transformSession !== originalTransformSession || transformEnabled !== originalTransformEnabled) {
      payload.transform_session = transformSession;
    }
    payload.transform_enabled = transformEnabled;

    WorkflowService.updateWorkflow(Number(workflowId), payload, () => {}, apiKey).subscribe(
      (updated: Workflow) => {
        setWorkflow(updated);
        setOriginalScript(script);
        setOriginalInputSchema(inputSchema);
        setOriginalOutputSchema(outputSchema);
        setOriginalTransformSession(transformSession);
        setOriginalTransformEnabled(transformEnabled);
        setSaving(false);
        toast.success("Workflow saved");
      },
      (err: any) => {
        setSaving(false);
        toast.error(`Failed to save: ${err?.message || "Unknown error"}`);
      }
    );
  };

  const handleSaveSettings = () => {
    if (!apiKey || !workflowId) return;
    setSavingSettings(true);
    WorkflowService.updateWorkflow(
      Number(workflowId),
      { workflow_name: settingsName, description: settingsDescription, timeout_seconds: settingsTimeout, application_id: settingsApplicationId },
      () => {},
      apiKey
    ).subscribe(
      (updated: Workflow) => { setWorkflow(updated); setSavingSettings(false); toast.success("Settings saved"); },
      (err: any) => { setSavingSettings(false); toast.error(`Failed to save: ${err?.message || "Unknown error"}`); }
    );
  };

  const handleDelete = () => {
    if (!apiKey || !workflowId) return;
    WorkflowService.deleteWorkflow(Number(workflowId), () => {}, apiKey).subscribe(
      () => { toast.success("Workflow deleted"); navigate("/forge/workflows"); },
      (err: any) => toast.error(`Failed to delete: ${err?.message || "Unknown error"}`)
    );
  };

  const hasChanges =
    script !== originalScript ||
    inputSchema !== originalInputSchema ||
    outputSchema !== originalOutputSchema ||
    transformSession !== originalTransformSession ||
    transformEnabled !== originalTransformEnabled;

  // --- Loading / not found states ---

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-6">
          <Card className="border-border">
            <CardContent className="p-8">
              <nav className="mb-6">
                <div className="flex items-center gap-2">
                  <div className="h-4 bg-muted rounded w-12 animate-pulse" />
                  <span className="text-muted-foreground/40">/</span>
                  <div className="h-4 bg-muted rounded w-12 animate-pulse" />
                  <span className="text-muted-foreground/40">/</span>
                  <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                  <span className="text-muted-foreground/40">/</span>
                  <div className="h-4 bg-muted rounded w-28 animate-pulse" />
                </div>
              </nav>
              <div className="space-y-2">
                <div className="h-9 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-48 animate-pulse" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-0">
              <div className="border-b border-border">
                <nav className="flex space-x-8 px-8 py-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="h-4 w-4 bg-muted rounded animate-pulse" />
                      <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    </div>
                  ))}
                </nav>
              </div>
              <div className="p-8 space-y-4">
                <div className="h-5 bg-muted rounded w-32 animate-pulse" />
                <div className="h-[400px] bg-muted/50 rounded-lg animate-pulse" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <p className="text-muted-foreground">Workflow not found.</p>
        </div>
      </div>
    );
  }

  // --- Main render ---

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-6">
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li><button onClick={() => navigate("/")} className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">Home</button></li>
                <li className="text-muted-foreground/40">/</li>
                <li><button onClick={() => navigate("/forge")} className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">Forge</button></li>
                <li className="text-muted-foreground/40">/</li>
                <li><button onClick={() => navigate("/forge/workflows")} className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">Workflows</button></li>
                <li className="text-muted-foreground/40">/</li>
                <li><span className="text-maitai-green font-gc-med">{workflow.workflow_name}</span></li>
              </ol>
            </nav>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-3xl font-gc-demi-bold text-foreground tracking-tight">
                  {workflow.workflow_name}
                </h1>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <code className="bg-muted px-2 py-0.5 rounded font-mono text-xs">
                    workflow:{workflow.workflow_ref_name}
                  </code>
                  {workflow.description && (
                    <>
                      <span className="text-muted-foreground/40">|</span>
                      <span>{workflow.description}</span>
                    </>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/forge/workflows")} className="gap-1">
                <ArrowLeftIcon className="w-4 h-4" />
                Back
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-0">
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-8 py-4">
                {[
                  { key: "script", label: "Script", icon: <CodeIcon className="w-4 h-4" /> },
                  { key: "sessions", label: "Sessions", icon: <HistoryIcon className="w-4 h-4" /> },
                  { key: "configuration", label: "Configuration", icon: <SettingsIcon className="w-4 h-4" /> },
                  { key: "versions", label: "Versions", icon: <GitBranchIcon className="w-4 h-4" /> },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setActiveTab(tab.key);
                      if (tab.key === "versions") loadVersions();
                    }}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                      activeTab === tab.key
                        ? "bg-maitai-green/10 text-maitai-green"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-8">
              {activeTab === "script" && (
                <WorkflowScriptTab
                  scriptSubTab={scriptSubTab}
                  setScriptSubTab={setScriptSubTab}
                  script={script}
                  setScript={setScript}
                  scriptLoading={scriptLoading}
                  inputSchema={inputSchema}
                  setInputSchema={setInputSchema}
                  outputSchema={outputSchema}
                  setOutputSchema={setOutputSchema}
                  transformSession={transformSession}
                  setTransformSession={setTransformSession}
                  transformEnabled={transformEnabled}
                  setTransformEnabled={setTransformEnabled}
                  hasChanges={hasChanges}
                  saving={saving}
                  onSave={handleSaveScript}
                />
              )}

              {activeTab === "versions" && (
                <WorkflowVersionsTab
                  versions={versions}
                  versionsLoading={versionsLoading}
                  deployingVersionId={deployingVersionId}
                  onDeploy={handleDeployVersion}
                />
              )}

              {activeTab === "sessions" && (
                <WorkflowSessionsTab
                  runs={runs}
                  runsTotal={runsTotal}
                  runsPage={runsPage}
                  setRunsPage={setRunsPage}
                  runsLoading={runsLoading}
                  onSelectRun={setSelectedRun}
                />
              )}

              {activeTab === "configuration" && (
                <WorkflowConfigTab
                  workflow={workflow}
                  settingsName={settingsName}
                  setSettingsName={setSettingsName}
                  settingsDescription={settingsDescription}
                  setSettingsDescription={setSettingsDescription}
                  settingsTimeout={settingsTimeout}
                  setSettingsTimeout={setSettingsTimeout}
                  settingsApplicationId={settingsApplicationId}
                  setSettingsApplicationId={setSettingsApplicationId}
                  applications={applications}
                  savingSettings={savingSettings}
                  onSave={handleSaveSettings}
                  onDelete={() => setDeleteOpen(true)}
                  onNewApplication={() => setShowNewAppModal(true)}
                />
              )}
            </div>
          </CardContent>
        </Card>

        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-gc-med">Delete Workflow</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{workflow.workflow_name}&quot;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <WorkflowRunDetailModal
          run={selectedRun}
          open={selectedRun !== null}
          onOpenChange={(open) => { if (!open) setSelectedRun(null); }}
        />

        <NewApplicationModal
          isOpen={showNewAppModal}
          onClose={() => setShowNewAppModal(false)}
          onApplicationCreated={(newApp: Application) => {
            setApplications((prev) => [...prev, newApp]);
            setSettingsApplicationId(newApp.id);
          }}
        />
      </div>
    </div>
  );
};

export default WorkflowDetail;
